import { ThumbsUp, MessageCircle, Share2, Copy, Sparkles } from 'lucide-react'
import { selectCurrentBusiness } from 'modules/business/slices/business-entities.slice'
import { useAppSelector } from 'modules/core'
import { useCallback } from 'react'
import { useBrandKitStore } from '../store/brand-kit-store'
import {
  useActiveEditingSectionStore,
  useEditorFocusStore,
  useSectionContextStore,
} from '../store/conversation-store'
import type { FacebookPostContent } from '../types'
import type { MobileSelectedSection } from '../components/mobile-selection-types'
import { EditsApplyingBadge } from '../components/EditsApplyingBadge'
import { MobileSectionSelectButton } from '../components/MobileSectionSelectButton'
import { GeneratingMediaOverlay } from '../components/AssetMedia'
import { FeedPreviewReelFallback } from './FeedPreviewReelFallback'
import { CarouselMediaPreview } from './CarouselMediaPreview'
import { getFeedVideoPreviewState, isSocialPostEmpty } from './preview-utils'
import { SectionHoverWrapper } from './SectionHoverWrapper'
import { EditableInlineText } from './EditableInlineText'
import { SectionActionBar } from './SectionActionBar'
import { CanvasMediaWithBar, useHasComposerCanvas } from '../composition/editor/ComposerCanvasMedia'
import { VideoBeatCanvas } from '../composition/editor/VideoTimeline'
import { useCarouselDeck, useVideoTimeline } from '../composition/editor/CompositionEditorProvider'
import { useCompositionLoading } from '../composition/editor/use-composition-loading'
import { useIsMobile } from 'hooks/use-mobile'
import { useComposerReadOnly } from '../components/composer-read-only'
import { useCreativeStore } from '../store/creative-store'

const SKEL = 'rgba(15, 23, 42, 0.06)'
const SKEL_BG =
  'linear-gradient(110deg, rgba(15,23,42,0.03) 8%, rgba(15,23,42,0.07) 18%, rgba(15,23,42,0.03) 33%)'

interface FacebookPostPreviewProps {
  content: FacebookPostContent
  generatingFields?: Set<string>
  creativeId?: string
  selectedSectionId?: string | null
  onSectionSelect?: (section: MobileSelectedSection | null) => void
  interactive?: boolean
}

export function FacebookPostPreview({
  content,
  generatingFields,
  creativeId,
  selectedSectionId,
  onSectionSelect,
  interactive = true,
}: FacebookPostPreviewProps) {
  const business = useAppSelector(selectCurrentBusiness)
  const logoUrl = useBrandKitStore(s => s.brandKit.logoId)
  const businessName = business?.name || 'Your Business'
  const setContext = useSectionContextStore(s => s.setContext)
  const requestFocus = useEditorFocusStore(s => s.requestFocus)
  const activeField = useActiveEditingSectionStore(s =>
    creativeId ? s.activeSections[creativeId] : null
  )

  const initials = businessName
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase()

  const mediaItems = content.mediaItems
  const carouselItems = mediaItems ?? []
  const isCarousel = Array.isArray(mediaItems) && mediaItems.length >= 2
  const hasImage = !!content.imageUrl
  const hasCanvas = useHasComposerCanvas()
  // A carousel with an editable deck composition → the editable deck editor (filmstrip + active
  // slide canvas), mirroring Instagram. Falls back to the read-only CarouselMediaPreview for
  // legacy decks / read-only / mobile.
  const deck = useCarouselDeck()
  const canvasCarousel = isCarousel && !!deck && interactive
  // A reel with an editable video Composition → the editable timeline; else read-only fallback.
  const timeline = useVideoTimeline()
  const canvasTimeline = !!timeline && interactive
  // Suppress the legacy hover while the composition is still loading, so it doesn't flash before
  // the live canvas mounts in its place.
  const compLoading = useCompositionLoading(creativeId)
  const hasReadyVideo = !!content.videoUrl
  const hasMessage = !!content.message
  const isMobile = useIsMobile()
  const readOnly = useComposerReadOnly()
  const updateCreativeContent = useCreativeStore(s => s.updateCreativeContent)
  const editable = !!creativeId && !readOnly && !isMobile

  const genImage = generatingFields?.has('image') ?? false
  const genVideo = generatingFields?.has('video') ?? false
  const genMessage = generatingFields?.has('message') ?? false
  const isGenerating = (generatingFields?.size ?? 0) > 0
  const { isGeneratingVideo, isFailedReel, isVideoMode, showCardOverlay } =
    getFeedVideoPreviewState(content, isGenerating, content.imageUrl)

  const handleSectionSelect = useCallback(
    (sectionType: string, label: string, preview: string) => {
      if (!onSectionSelect || !creativeId) return
      if (selectedSectionId === sectionType) {
        onSectionSelect(null)
      } else {
        onSectionSelect({
          sectionId: sectionType,
          sectionType,
          creativeType: 'facebook_post',
          label,
          preview,
        })
      }
    },
    [creativeId, selectedSectionId, onSectionSelect]
  )

  const handleAskAI = useCallback(
    (field: string, label: string, preview: string) => {
      if (creativeId) {
        setContext(creativeId, { sectionId: field, sectionType: field, label, preview })
      }
    },
    [creativeId, setContext]
  )

  const handleEdit = useCallback(
    (field: string) => {
      if (creativeId) {
        requestFocus(creativeId, field)
      }
    },
    [creativeId, requestFocus]
  )

  if (isSocialPostEmpty(content)) {
    return (
      <div className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-6">
        <div className="flex flex-col items-center justify-center gap-3 py-12 text-gray-400">
          <span className="text-4xl">📘</span>
          <p className="text-sm">Your Facebook post will appear here</p>
        </div>
      </div>
    )
  }

  // Reels render the media at a fixed 9:16 / 320px phone-frame. Shrink the
  // card to match so the header avatar/name don't sit left of the video
  // edge — see the matching note in InstagramPostPreview.

  return (
    <div
      className={`relative mx-auto w-full overflow-hidden rounded-lg bg-white transition-all duration-300 ${isVideoMode ? 'max-w-[320px]' : 'max-w-lg'}`}
      style={{
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}
    >
      {showCardOverlay && (
        <GeneratingMediaOverlay
          shape={isCarousel ? 'carousel' : isVideoMode ? 'video' : 'post'}
          tone="light"
          radius={8}
        />
      )}
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={businessName}
            className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-xs font-bold text-white">
            {initials}
          </div>
        )}
        <div>
          <p className="text-sm font-semibold text-gray-900">{businessName}</p>
        </div>
      </div>

      {/* Message — inline-editable on desktop; mobile keeps its tap→sheet flow. */}
      {editable && !genMessage ? (
        <SectionActionBar
          actions={[
            {
              icon: <Copy size={12} />,
              label: 'Copy',
              confirmLabel: 'Copied',
              onClick: () =>
                navigator.clipboard
                  .writeText(content.message ?? '')
                  .catch(err => console.error('copy message failed:', err)),
            },
            {
              icon: <Sparkles size={12} />,
              label: 'Ask Taka',
              onClick: () =>
                handleAskAI('message', 'Message', (content.message ?? '').slice(0, 80)),
            },
          ]}
        >
          <div className="px-4 pb-3">
            <EditableInlineText
              initialValue={content.message ?? ''}
              onCommit={t => updateCreativeContent(creativeId!, { message: t })}
              ariaLabel="Edit post message"
              placeholder="Write something…"
              className="block text-sm whitespace-pre-line text-gray-900 outline-none focus:outline-none"
            />
          </div>
        </SectionActionBar>
      ) : hasMessage ? (
        <div
          className={`relative cursor-pointer transition-all duration-200 md:cursor-default ${selectedSectionId === 'message' ? 'ring-2 ring-[color:var(--acid)]/30 ring-inset' : ''}`}
          onClick={() =>
            onSectionSelect &&
            handleSectionSelect('message', 'Message', content.message.slice(0, 80))
          }
        >
          <div className="relative overflow-hidden px-4 pb-3">
            <p className="text-sm whitespace-pre-line text-gray-900">{content.message}</p>
          </div>
          {!genMessage && onSectionSelect && interactive && (
            <MobileSectionSelectButton
              selected={selectedSectionId === 'message'}
              onSelect={() =>
                handleSectionSelect('message', 'Message', content.message.slice(0, 80))
              }
            />
          )}
        </div>
      ) : genMessage ? (
        <div className="relative overflow-hidden px-4 pb-3">
          <div className="flex flex-col gap-2.5">
            <div className="h-2.5 w-full rounded-md" style={{ background: SKEL }} />
            <div className="h-2.5 w-3/4 rounded-md" style={{ background: SKEL }} />
          </div>
        </div>
      ) : null}

      {/* Image / Video / Carousel */}
      {canvasTimeline ? (
        <VideoBeatCanvas onTap={() => handleSectionSelect('image', 'Video', 'Reel')} />
      ) : hasCanvas && hasImage && !isVideoMode && !isCarousel ? (
        <CanvasMediaWithBar
          clipClassName="w-full overflow-hidden"
          interactive={interactive}
          onTap={() => handleSectionSelect('image', 'Image', 'Post image')}
        />
      ) : canvasCarousel ? (
        <CanvasMediaWithBar
          clipClassName="w-full overflow-hidden"
          onTap={() => handleSectionSelect('image', 'Carousel', `${carouselItems.length} slides`)}
        />
      ) : isCarousel ? (
        <div className="relative w-full overflow-hidden bg-white">
          <CarouselMediaPreview items={mediaItems} altText={content.altText} />
          {/* Read-only deck fallback: the rendered slides lag the user's
              composition edits until the master re-renders — say so. */}
          {content.compositionPendingRender && !isGenerating && (
            <EditsApplyingBadge overlay creativeId={creativeId} />
          )}
        </div>
      ) : (
        <div
          className={`relative cursor-pointer overflow-hidden transition-all duration-200 md:cursor-default ${
            isVideoMode ? 'aspect-[9/16] w-full bg-black' : 'w-full'
          } ${selectedSectionId === 'image' ? 'ring-2 ring-[color:var(--acid)]/30 ring-inset' : ''}`}
          onClick={() =>
            onSectionSelect &&
            handleSectionSelect(
              'image',
              hasReadyVideo ? 'Video' : 'Image',
              hasReadyVideo ? 'Post video' : 'Post image'
            )
          }
        >
          {hasReadyVideo ? (
            <>
              {!genVideo && creativeId ? (
                <SectionHoverWrapper
                  fieldId="video"
                  onAskAI={() => handleAskAI('video', 'Video', 'Post video')}
                  onEdit={() => handleEdit('video')}
                  isActiveEditing={activeField === 'video'}
                >
                  <video
                    src={content.videoUrl ?? undefined}
                    className="h-full w-full object-cover"
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                </SectionHoverWrapper>
              ) : (
                <video
                  src={content.videoUrl ?? undefined}
                  className="h-full w-full object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              )}
              {!genVideo && onSectionSelect && interactive && (
                <MobileSectionSelectButton
                  selected={selectedSectionId === 'image'}
                  onSelect={() => handleSectionSelect('image', 'Video', 'Post video')}
                />
              )}
            </>
          ) : isFailedReel || (isGeneratingVideo && content.imageUrl) ? (
            <FeedPreviewReelFallback
              posterUrl={content.imageUrl}
              isFailedReel={isFailedReel}
              isGeneratingVideo={isGeneratingVideo}
            />
          ) : hasImage ? (
            <>
              {!genImage && creativeId && !compLoading ? (
                <SectionHoverWrapper
                  fieldId="image"
                  onAskAI={() => handleAskAI('image', 'Image', 'Post image')}
                  onEdit={() => handleEdit('image')}
                  isActiveEditing={activeField === 'image'}
                >
                  <img
                    src={content.imageUrl ?? undefined}
                    alt={content.altText || 'Post image'}
                    className="w-full object-cover"
                  />
                </SectionHoverWrapper>
              ) : (
                <img
                  src={content.imageUrl ?? undefined}
                  alt={content.altText || 'Post image'}
                  className="w-full object-cover"
                />
              )}
              {!genImage && onSectionSelect && interactive && (
                <MobileSectionSelectButton
                  selected={selectedSectionId === 'image'}
                  onSelect={() => handleSectionSelect('image', 'Image', 'Post image')}
                />
              )}
            </>
          ) : genImage || genVideo ? (
            <div
              className="flex aspect-video w-full items-center justify-center"
              style={{ background: SKEL_BG }}
            >
              <div
                className="z-[1] flex h-16 w-16 items-center justify-center rounded-2xl"
                style={{ background: SKEL }}
              >
                <span className="text-2xl">{genVideo ? '🎬' : '🎨'}</span>
              </div>
            </div>
          ) : null}
          {/* Master-image fallback only (never the live canvas branches above):
            the rendered master lags the user's composition edits until the
            server re-renders it. Video branches excluded (flag never set). */}
          {content.compositionPendingRender &&
            !isGenerating &&
            !isVideoMode &&
            !hasReadyVideo &&
            hasImage && <EditsApplyingBadge overlay creativeId={creativeId} />}
        </div>
      )}

      {/* Engagement bar — presentational only (matches the Instagram preview's
          inert action row). The PostModal preview shouldn't tease interactions
          that don't fire anything. */}
      <div className="flex items-center justify-around border-t px-4 py-2">
        <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500">
          <ThumbsUp size={16} />
          Like
        </span>
        <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500">
          <MessageCircle size={16} />
          Comment
        </span>
        <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500">
          <Share2 size={16} />
          Share
        </span>
      </div>
    </div>
  )
}
